import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Palette, BookOpen, PenTool, CalendarDays, CheckSquare, Megaphone, BarChart3, Coins, Plug, CreditCard, Settings, Lock, LogOut, Shield, Building2, Inbox, Receipt, KeyRound, Menu, Radar, UserCircle2, Ticket, Mail, Users, MousePointerClick, CalendarRange } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

const APP = [
  ["/app", "Dashboard", LayoutDashboard, "dashboard"], ["/app/brand", "Brand Profile", Palette, "brand"], ["/app/knowledge", "Knowledge Base", BookOpen, "knowledge"],
  ["/app/studio", "Content Studio", PenTool, "content"], ["/app/calendar", "Content Calendar", CalendarDays, "calendar"], ["/app/approvals", "Approvals", CheckSquare, "approvals"],
  ["/app/campaigns", "Campaigns", Megaphone, "campaigns"], ["/app/leads", "Leads & Tracking", MousePointerClick, "leads"], ["/app/analytics", "Analytics", BarChart3, "analytics"],
  ["/app/intel", "Market Intelligence", Radar, "intel"], ["/app/personas", "Customer Personas", UserCircle2, "personas"], ["/app/events", "Industry Events", Ticket, "events"],
  ["/app/digests", "Weekly Digest", Mail, null], ["/app/team", "Team", Users, "team"], ["/app/credits", "Credits Wallet", Coins, null],
  ["/app/integrations", "Integrations", Plug, "integrations"], ["/app/billing", "Billing", CreditCard, null], ["/app/settings", "Settings", Settings, null],
];
const ADMIN = [
  ["/admin", "Overview", Shield], ["/admin/companies", "Companies", Building2], ["/admin/enquiries", "Enquiries", Inbox],
  ["/admin/cms", "Website CMS", Palette], ["/admin/users", "All users", Users], ["/admin/reports", "Reports & activity", BarChart3],
  ["/admin/payments", "Payments", Receipt], ["/admin/events", "Events", CalendarRange], ["/admin/settings", "Payment keys & logs", KeyRound],
];

const Item = ({ to, label, Icon, locked, onClick }) => (
  <NavLink to={to} end={to === "/app" || to === "/admin"} onClick={onClick} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#4f46e5]/12 text-[#4f46e5] dark:text-[#a5b4fc]" : "text-foreground/70 hover:bg-foreground/5 hover:text-foreground"}`}>
    <Icon className="h-4 w-4" /><span className="flex-1">{label}</span>{locked && <Lock className="h-3.5 w-3.5 opacity-60" />}
  </NavLink>
);

export default function AppLayout() {
  const { user, company, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const mods = company?.modules || [];
  const side = (
    <div className="flex h-full flex-col">
      <Link to="/" className="px-3 py-2 text-foreground" data-testid="app-logo-link"><Logo className="h-8" /></Link>
      <div className="mt-6 rounded-xl border bg-background/60 p-3">
        <div className="text-sm font-semibold truncate" data-testid="sidebar-company-name">{company?.name}</div>
        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground"><span data-testid="sidebar-plan-name">{company?.plan_name} · <span className="capitalize">{user?.role}</span></span><span data-testid="sidebar-credits">{company?.credits?.toLocaleString()} credits</span></div>
      </div>
      <nav className="mt-5 flex-1 space-y-1 overflow-y-auto">
        {APP.map(([to, l, I, m]) => <Item key={to} to={to} label={l} Icon={I} locked={m && !mods.includes(m)} onClick={() => setOpen(false)} />)}
        {user?.role === "admin" && (
          <>
            <div className="eyebrow px-3 pt-6 pb-2">Admin console</div>
            {ADMIN.map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} onClick={() => setOpen(false)} />)}
          </>
        )}
      </nav>
      <div className="mt-4 flex items-center justify-between border-t pt-4">
        <div className="min-w-0"><div className="text-sm font-medium truncate">{user?.name}</div><div className="text-xs text-muted-foreground truncate">{user?.email}</div></div>
        <div className="flex gap-1">
          <ThemeToggle testId="app-theme-toggle" />
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/"); }} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10 hover:bg-foreground/5" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[272px_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen border-r bg-card/50 p-4">{side}</aside>
      <div className="lg:hidden sticky top-0 z-40 glass border-x-0 border-t-0 flex items-center justify-between px-4 h-16">
        <Logo className="h-7 text-foreground" testId="app-mobile-logo" />
        <button onClick={() => setOpen(!open)} data-testid="app-mobile-menu" className="h-10 w-10 inline-flex items-center justify-center rounded-full border"><Menu className="h-4 w-4" /></button>
      </div>
      {open && <div className="lg:hidden fixed inset-0 z-50 bg-background p-4">{side}</div>}
      <main className="min-w-0 p-5 sm:p-8 lg:p-10"><Outlet /></main>
    </div>
  );
}
